/**
 * CompliancePanel — #4 Document expiry / compliance dashboard (admin-only).
 *
 * Read-only fleet view of driver compliance document expiry, from
 * GET /api/admin/compliance/dashboard. Shows summary counts (expiring
 * ≤30/≤14/≤7 days + expired) and a per-document table with driver name,
 * document type, expiry date, days remaining and a green/amber/red status.
 *
 * Expired mandatory docs block going online (server-side). Admins record expiry dates from
 * the document reviewer (Approvals tab); drivers are reminded by email at the
 * 30/14/7/1-day milestones. Touches no protected dispatch areas.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { ComplianceImpactPanel } from "./ComplianceImpactPanel";
import { ComplianceEnforcementSwitch } from "./ComplianceEnforcementSwitch";
import {
  ShieldCheck, RefreshCw, CalendarClock, AlertTriangle, FileWarning,
  CircleSlash,
} from "lucide-react";

const POLL_MS = 30000;

const STATUS_MAP = {
  expired:  ["EXPIRED",  "#991B1B", "#FEE2E2"],
  expiring: ["EXPIRING", "#92400E", "#FEF3C7"],
  valid:    ["VALID",    "#065F46", "#D1FAE5"],
  unset:    ["NO EXPIRY","#374151", "#F3F4F6"],
};

const StatusPill = ({ status }) => {
  const [label, fg, bg] = STATUS_MAP[status] || STATUS_MAP.unset;
  return (
    <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm" style={{ color: fg, background: bg }}
      data-testid={`compliance-status-${status}`}>
      {label}
    </span>
  );
};

const daysLabel = (d) => {
  if (d === null || d === undefined) return "—";
  if (d < 0) return `${Math.abs(d)}d ago`;
  if (d === 0) return "today";
  return `${d}d`;
};

const SummaryCard = ({ label, value, accent = "#0A0A0A", icon, testid }) => (
  <div className="bg-white border border-border p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="label-mini text-muted-foreground">{label}</div>
      {icon && <span style={{ color: accent }}>{icon}</span>}
    </div>
    <div className="font-heading text-3xl font-black mt-1" style={{ color: accent }} data-testid={`${testid}-value`}>
      {value}
    </div>
  </div>
);

export function CompliancePanel() {
  const [impactKey, setImpactKey] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.get("/admin/compliance/dashboard");
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr("Couldn't load compliance data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, POLL_MS);
    return () => clearInterval(t);
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="p-8 text-center text-muted-foreground" data-testid="compliance-loading">
        <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading compliance data…
      </div>
    );
  }
  if (err && !data) {
    return <div className="p-8 text-center text-[#991B1B]" data-testid="compliance-error">{err}</div>;
  }

  const fleet = data.fleet, items = data.items || [];

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="compliance-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#002FA7]" />
          <div>
            <h2 className="font-heading text-2xl font-black tracking-tight flex items-center gap-2">
              Document Compliance
              {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" data-testid="compliance-refreshing" />}
            </h2>
            <div className="text-xs text-muted-foreground">
              Licence · badge · insurance · vehicle · right to work · DBS · MOT expiry · {data.timezone} · today {data.today} · updates every 30s
            </div>
          </div>
        </div>
      </div>

      {/* Expired / expiring alert banner (warning-only) */}
      {(fleet.expired > 0 || fleet.expiring > 0) && (
        <div
          className={`flex items-start gap-2 border px-4 py-3 ${fleet.expired > 0 ? "border-[#FCA5A5] bg-[#FEF2F2]" : "border-[#FDE68A] bg-[#FFFBEB]"}`}
          data-testid="compliance-alert-banner"
        >
          <AlertTriangle className={`w-5 h-5 mt-0.5 ${fleet.expired > 0 ? "text-[#991B1B]" : "text-[#92400E]"}`} />
          <div className="text-sm">
            <span className="font-bold">
              {fleet.expired > 0 && `${fleet.expired} document${fleet.expired > 1 ? "s" : ""} expired`}
              {fleet.expired > 0 && fleet.expiring > 0 && " · "}
              {fleet.expiring > 0 && `${fleet.expiring} expiring within 30 days`}
            </span>
            <span className="text-muted-foreground"> — across {fleet.drivers_affected} driver{fleet.drivers_affected === 1 ? "" : "s"}. Drivers are reminded by email; when enforcement is ON, an expired mandatory document blocks going online.</span>
          </div>
        </div>
      )}

      <ComplianceEnforcementSwitch onChange={() => setImpactKey((k) => k + 1)} />
      <ComplianceImpactPanel key={impactKey} />

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="compliance-fleet">
        <SummaryCard label="EXPIRED" value={fleet.expired}
          accent={fleet.expired > 0 ? "#EF4444" : "#0A0A0A"}
          icon={<FileWarning className="w-4 h-4" />} testid="compliance-expired" />
        <SummaryCard label="EXPIRING ≤7 DAYS" value={fleet.in_7}
          accent={fleet.in_7 > 0 ? "#EF4444" : "#0A0A0A"}
          icon={<CalendarClock className="w-4 h-4" />} testid="compliance-in7" />
        <SummaryCard label="EXPIRING ≤14 DAYS" value={fleet.in_14}
          accent={fleet.in_14 > 0 ? "#F59E0B" : "#0A0A0A"}
          icon={<CalendarClock className="w-4 h-4" />} testid="compliance-in14" />
        <SummaryCard label="EXPIRING ≤30 DAYS" value={fleet.in_30}
          accent={fleet.in_30 > 0 ? "#F59E0B" : "#0A0A0A"}
          icon={<CalendarClock className="w-4 h-4" />} testid="compliance-in30" />
      </div>

      {/* Secondary summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <SummaryCard label="VALID" value={fleet.valid} accent="#10B981"
          icon={<ShieldCheck className="w-4 h-4" />} testid="compliance-valid" />
        <SummaryCard label="NO EXPIRY SET" value={fleet.unset}
          accent={fleet.unset > 0 ? "#6B7280" : "#0A0A0A"}
          icon={<CircleSlash className="w-4 h-4" />} testid="compliance-unset" />
        <SummaryCard label="DOCUMENTS TRACKED" value={fleet.total_docs} accent="#0A0A0A"
          icon={<ShieldCheck className="w-4 h-4" />} testid="compliance-total" />
      </div>

      {/* Per-document table */}
      <div className="bg-white border border-border overflow-x-auto" data-testid="compliance-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary">
              <th className="px-3 py-2 text-left label-mini text-muted-foreground">Driver</th>
              <th className="px-3 py-2 text-left label-mini text-muted-foreground">Document</th>
              <th className="px-3 py-2 text-left label-mini text-muted-foreground">Expiry date</th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">Days left</th>
              <th className="px-3 py-2 text-center label-mini text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground italic" data-testid="compliance-empty">No compliance documents uploaded yet.</td></tr>
            ) : items.map((it) => {
              const fg = it.status === "expired" ? "#991B1B"
                : it.status === "expiring" ? "#92400E"
                : it.status === "valid" ? "#065F46" : "#6B7280";
              return (
                <tr key={`${it.driver_id}-${it.doc_type}`} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid="compliance-row">
                  <td className="px-3 py-2 font-bold" data-testid="compliance-row-name">{it.driver_name}</td>
                  <td className="px-3 py-2">{it.doc_label}</td>
                  <td className="px-3 py-2 font-mono-tab">{it.expiry_date || <span className="text-muted-foreground italic">not set</span>}</td>
                  <td className="px-3 py-2 text-right font-mono-tab font-bold" style={{ color: fg }}>{daysLabel(it.days_remaining)}</td>
                  <td className="px-3 py-2 text-center"><StatusPill status={it.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-4" data-testid="compliance-legend">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ background: "#10B981" }} /> Valid (&gt;30 days)</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ background: "#F59E0B" }} /> Expiring (≤30 days)</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ background: "#EF4444" }} /> Expired</span>
        <span className="italic">Set expiry dates in the Approvals tab when reviewing a driver's documents. Drivers are reminded by email at 30/14/7/1 days and on expiry.</span>
      </div>
    </div>
  );
}

export default CompliancePanel;
